import { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { IN_APP_FOOTER_LINKS } from './publicFooterLinks';

// Modeled on kolega.dev/privacy (KLG Tech Innovations Limited) and adapted for Kolega Studio.
// Payment processors: CityPay (legacy) and Paddle (Merchant of Record) — flagged for sign-off.
function PublicPageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-white flex flex-col">
      <header className="border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-base sm:text-lg font-bold flex items-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1.5 sm:mr-2">
              <path d="M7 8L3 12L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17 8L21 12L17 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 20L14 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Kolega</span>
            <span className="font-light">Studio</span>
          </Link>
          <Link
            to="/"
            className="bg-brand-primary text-black text-sm font-medium px-4 py-2 rounded-lg hover:bg-brand-primaryDark transition-colors"
          >
            Sign in
          </Link>
        </div>
      </header>
      <div className="flex-1">{children}</div>
      <footer className="border-t border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-600 dark:text-neutral-400">
          {IN_APP_FOOTER_LINKS.map((link) => (
            <Link key={link.href} to={link.href} className="hover:text-neutral-900 dark:hover:text-white transition-colors">
              {link.label}
            </Link>
          ))}
        </div>
        <div className="pb-6 text-center text-xs text-neutral-500 dark:text-neutral-500">
          &copy; {new Date().getFullYear()} Kolega Studio. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

export default function PrivacyPage() {
  return (
    <PublicPageShell>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-neutral-700 dark:text-neutral-300">
        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">Privacy Policy</h1>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-8">Last Updated: August 11, 2026</p>

        <div className="space-y-6 text-sm">
          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Introduction</h3>
            <p className="mb-3">
              KLG Tech Innovations Limited (&ldquo;Kolega,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;) respects your privacy and is
              committed to protecting your personal data. This Privacy Policy explains how we
              collect, use, and safeguard your information when you use Kolega Studio at
              studio.app.kolega.co.uk (the &ldquo;Service&rdquo;).
            </p>
            <p className="mb-3">
              If you use Kolega Studio through your employer, their privacy policies may also apply.
              For information about cookies and tracking technologies, please see our separate Cookie Policy.
            </p>
            <p>For questions about this policy, contact: contact@kolega.ai</p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Personal Data We Collect</h3>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">Information You Provide</h4>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Account Information:</strong> Name, email address, and password details you provide when you create an account.</li>
              <li><strong>Payment Information:</strong> Billing details and payment method information for paid subscriptions and credit purchases. This information is securely transmitted to and processed by our payment providers, CityPay and Paddle (our payment processor and Merchant of Record).</li>
              <li><strong>Communications:</strong> Content of your messages when you contact us for support or feedback, including support requests submitted through the Service.</li>
              <li><strong>Project Content:</strong> Prompts, code, files, and other content you create, upload, or generate in your workspaces and projects. This content is stored so your projects persist between sessions.</li>
              <li><strong>Domain Information:</strong> Registrant details you provide when purchasing a custom domain through the Service, which are shared with the domain registrar as required to complete registration.</li>
            </ul>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">Information Collected Automatically</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system, and connection details.</li>
              <li><strong>Usage Data:</strong> Features used, actions taken, timestamps, errors encountered, performance metrics, and session statistics.</li>
              <li><strong>Billing and Credit Data:</strong> Subscription status, credit balances, credit consumption, and purchase history.</li>
              <li><strong>Location Data:</strong> Approximate location based on IP address for service optimization and security.</li>
              <li><strong>Cookies and Trackers:</strong> We use cookies and other tracking technologies to enhance your experience. For detailed information about cookies, please see our Cookie Policy.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">How We Use Your Data</h3>
            <p className="mb-3">We process your personal data to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide and maintain our Service</li>
              <li>Manage your account, subscriptions, and credit balances</li>
              <li>Process payments and prevent fraud</li>
              <li>Communicate service updates and respond to inquiries</li>
              <li>Improve our Service through analytics</li>
              <li>Ensure security and prevent abuse</li>
              <li>Comply with legal obligations</li>
              <li>Enforce our Terms of Service</li>
              <li>Traffic optimization and distribution</li>
              <li>Hosting and backend infrastructure</li>
              <li>Analytics and monitoring web traffic</li>
            </ul>
            <p className="mt-3 font-medium text-brand-primaryDark dark:text-brand-primary">
              Important: We do not use your project content to train AI models.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Legal Basis for Processing (EEA/UK Users)</h3>
            <p className="mb-3">We process your data based on:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Contract:</strong> To provide the Service you&apos;ve requested</li>
              <li><strong>Consent:</strong> For marketing communications and optional features</li>
              <li><strong>Legal Obligation:</strong> To comply with laws and regulations</li>
              <li><strong>Legitimate Interests:</strong> For security, fraud prevention, and service improvement</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Data Sharing</h3>
            <p className="mb-3">We share your data only with:</p>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">Service Providers</h4>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Amazon Web Services (AWS):</strong> Cloud infrastructure, hosting, storage, backend infrastructure, email delivery, and traffic optimization</li>
              <li><strong>Paddle:</strong> Payment processing, billing, and Merchant of Record</li>
              <li><strong>CityPay:</strong> Payment processing for legacy subscriptions</li>
              <li><strong>AI Model Providers:</strong> Anthropic and OpenAI (with zero data retention agreements) for code and content generation</li>
              <li><strong>SendGrid (Twilio):</strong> Transactional and support email delivery</li>
              <li><strong>GitHub:</strong> Repository access and code export, when you connect your GitHub account</li>
              <li><strong>Domain Registrars:</strong> Registration and management of custom domains you purchase through the Service</li>
              <li><strong>Analytics Providers:</strong> Product analytics and web traffic monitoring</li>
              <li><strong>Internal Communications:</strong> Slack and Google Workspace may be used for customer support where team members may access support tickets and communications</li>
            </ul>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">Other Disclosures</h4>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Legal Requirements:</strong> When required by law or legal process</li>
              <li><strong>Business Transfers:</strong> During mergers or acquisitions</li>
              <li><strong>With Consent:</strong> When you authorise specific sharing</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Data Security</h3>
            <p className="mb-3">
              We implement industry-standard technical and organisational measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Encryption in transit (TLS 1.3) and at rest</li>
              <li>Access controls and authentication</li>
              <li>Regular security reviews and improvements</li>
              <li>Incident response procedures</li>
              <li>Appropriate security measures to prevent unauthorised access, disclosure, modification, or unauthorised destruction of data</li>
            </ul>
            <p>
              While we continuously work to enhance our security measures, no system is completely secure.
              Please protect your account credentials and notify us immediately of any suspicious activity.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Project Content and AI Processing</h3>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">What We Store</h4>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>Your workspaces and projects, including the code and files that make up your applications</li>
              <li>Conversation history with AI agents in your workspaces</li>
              <li>Deployment configuration and metadata for your hosted applications</li>
              <li>Metadata including project names, timestamps, and usage records</li>
            </ul>

            <h4 className="text-md font-medium text-neutral-900 dark:text-white mb-2">AI Processing</h4>
            <p className="mb-3">Kolega Studio uses third-party AI providers to generate and edit application code:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li><strong>Relevant context only:</strong> AI models receive the prompts and project context needed to fulfil each request</li>
              <li><strong>No training:</strong> Your prompts, code, and content are not used to train or improve AI models</li>
              <li><strong>Temporary processing:</strong> Content sent to AI providers is processed and discarded per provider policies</li>
            </ul>
            <p>
              Model providers maintain zero data retention policies for API customers. Content submitted through
              Kolega Studio is processed in real-time and not stored, logged, or used for model training by those providers.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Your Privacy Rights</h3>
            <p className="mb-3">Depending on your location, you may have rights to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Access:</strong> Obtain copies of your personal data and learn if data is being processed</li>
              <li><strong>Rectification:</strong> Correct inaccurate information and verify the accuracy of your data</li>
              <li><strong>Deletion:</strong> Request removal of your data and have your personal data deleted or otherwise removed</li>
              <li><strong>Restriction:</strong> Limit how we process your data</li>
              <li><strong>Portability:</strong> Receive data in a portable format and have it transferred to another controller</li>
              <li><strong>Objection:</strong> Oppose certain processing activities</li>
              <li><strong>Withdrawal:</strong> Revoke consent where processing is consent-based</li>
            </ul>
            <p>To exercise these rights, contact: contact@kolega.ai</p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Additional Rights for EEA/UK Residents</h3>
            <p className="mb-3">You may lodge complaints with your local data protection authority:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>EU authorities:</strong> ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm</li>
              <li><strong>UK authority:</strong> ico.org.uk</li>
            </ul>
            <p className="mb-3">
              You are also entitled to learn about the legal basis for data transfers abroad including
              to any international organisation governed by public international law or set up by two or
              more countries, such as the UN, and about the security measures taken by the Owner to safeguard your data.
            </p>
            <p>
              Where personal data is processed for a public interest, in the exercise of an official
              authority vested in the Owner or for the purposes of the legitimate interests pursued by
              the Owner, users may object to such processing by providing a ground related to their
              particular situation to justify the objection.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Additional Rights for US Residents</h3>
            <p className="mb-3">Some US states provide additional rights including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Confirming data processing</li>
              <li>Opting out of sales/targeted advertising (we do not sell data)</li>
              <li>Non-discrimination for exercising rights</li>
              <li>Appealing privacy decisions</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Data Retention</h3>
            <p className="mb-3">We retain personal data only as long as necessary for:</p>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li>Providing our Service</li>
              <li>Complying with legal obligations</li>
              <li>Resolving disputes</li>
              <li>Enforcing agreements</li>
            </ul>
            <p className="mb-3">
              Unless specified otherwise, personal data shall be processed and stored for as long as
              required by the purpose they have been collected for and may be retained for longer due
              to applicable legal obligation or based on the users&apos; consent.
            </p>
            <p className="mb-3">
              Personal data collected for purposes related to the performance of a contract between
              the Owner and the user shall be retained until such contract has been fully performed.
              Personal data collected for the purposes of the Owner&apos;s legitimate interests shall be
              retained as long as needed to fulfill such purposes.
            </p>
            <p>
              Once the retention period expires, personal data shall be deleted. Therefore, the right
              of access, the right to erasure, the right to rectification and the right to data
              portability cannot be enforced after expiration of the retention period.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">International Data Transfers</h3>
            <p className="mb-3">
              Our servers are located in the United Kingdom and European Union. If you access our Service
              from elsewhere, your data may be transferred to those regions. For transfers outside the
              EEA/UK, all data is encrypted in transit and at rest and complies with Service Provider
              Standard Contractual Clauses.
            </p>
            <p>
              Depending on the user&apos;s location, data transfers may involve transferring the user&apos;s
              data to a country other than their own. Data processing is carried out at the Owner&apos;s
              operating offices and in any other places where the parties involved in the processing are located.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Children&apos;s Privacy</h3>
            <p>
              Our Service is not intended for children under 18. If we discover we&apos;ve collected data
              from a child under 18, we&apos;ll promptly delete it. To report underage users, contact: contact@kolega.ai
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">System Logs and Maintenance</h3>
            <p>
              For operation and maintenance purposes, this application and any third-party services
              may collect files that record interaction with this application (System logs) or use
              other personal data (such as the IP address) for this purpose.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Legal Action</h3>
            <p>
              The user&apos;s personal data may be used for legal purposes by the Owner in Court or in the
              stages leading to possible legal action arising from improper use of this application or
              the related services. The user declares to be aware that the Owner may be required to
              reveal personal data upon request of public authorities.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Updates to This Policy</h3>
            <p className="mb-3">
              We may update this policy periodically. We&apos;ll notify you of material changes through
              our Service or email. Continued use after changes indicates acceptance.
            </p>
            <p>
              The Owner reserves the right to make changes to this privacy policy at any time by
              notifying its users on this page and possibly within this application and/or - as far
              as technically and legally feasible - sending a notice to users via any contact
              information available to the Owner.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">International Users</h3>
            <p>
              We welcome users worldwide. All users enjoy the privacy rights described in this policy,
              which meet or exceed international standards including GDPR. If you have questions about
              privacy rights in your jurisdiction, please contact us.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">Contact Information</h3>
            <p className="mb-3">For privacy questions or to exercise your rights:</p>
            <div className="bg-neutral-100 dark:bg-neutral-900 p-4 rounded-lg">
              <p className="font-medium text-neutral-900 dark:text-white">Data Controller:</p>
              <p className="font-medium text-neutral-900 dark:text-white">KLG Tech Innovations Limited</p>
              <p>Val Verclut, La Route des Cotils</p>
              <p>Grouville, JE3 9AP, Jersey</p>
              <p>Email: contact@kolega.ai</p>
            </div>
          </section>

          <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4 text-center text-xs text-neutral-500 dark:text-neutral-400">
            &copy; {new Date().getFullYear()} Kolega Studio. All rights reserved.
          </div>
        </div>
      </main>
    </PublicPageShell>
  );
}
